import { collectorText } from './collectorLabel.ts';

export function parseEvidenceDns(summary?: string): { type: string; value: string } {
  if (!summary) return { type: 'DNS', value: 'Observed' };
  const colon = summary.indexOf(':');
  const space = summary.indexOf(' ');
  // "TYPE: value" only; in "AAAA 2606:4700::1" the first colon belongs to the address.
  if (colon !== -1 && (space === -1 || colon < space)) {
    const type = summary.slice(0, colon).trim();
    const value = summary.slice(colon + 1).trim();
    if (type && value) return { type, value };
  }
  if (space !== -1) {
    const type = summary.slice(0, space).trim();
    const value = summary.slice(space + 1).trim();
    if (type && value) return { type, value };
  }
  return { type: 'DNS', value: summary };
}

export function parseNetworkEvidence(summary?: string): { type: string; value: string } {
  if (!summary) return { type: 'NET', value: 'Observed endpoint' };
  if (summary.startsWith('CT ')) return { type: 'CT', value: summary.slice(3).trim() };
  if (summary.startsWith('TCP ')) return { type: 'TCP', value: summary.slice(4).trim() };
  if (/^AS\d/i.test(summary) || summary.startsWith('AS ')) return { type: 'ASN', value: summary };
  if (summary.startsWith('PTR ')) return { type: 'PTR', value: summary.slice(4).trim() };
  return { type: 'NET', value: summary };
}

export function coverageForEvidence<
  T extends { coverageUnitId: string; target: string; sourceRecordIds: string[] },
>(
  coverage: T[],
  sourceRecords: { sourceRecordId: string; coverageUnitId?: string }[],
  sourceRecordId: string,
): T | undefined {
  const source = sourceRecords.find((item) => item.sourceRecordId === sourceRecordId);
  return coverage.find(
    (unit) =>
      unit.coverageUnitId === source?.coverageUnitId ||
      unit.coverageUnitId === sourceRecordId ||
      unit.sourceRecordIds.includes(sourceRecordId),
  );
}

/** "12 records · A ×2, AAAA ×2, MX ×3, TXT ×4 · 1 failed": every DNS outcome of an attempt. */
export function dnsOutcomeText(items: readonly { kind: string; summary?: string }[]): string {
  const counts = new Map<string, number>();
  let failed = 0;
  for (const item of items) {
    if (item.kind === 'ERROR_RECORD') failed += 1;
    else {
      const type =
        item.kind === 'DNS_RECORD'
          ? parseEvidenceDns(item.summary).type
          : item.kind.replaceAll('_', ' ').toLowerCase();
      counts.set(type, (counts.get(type) ?? 0) + 1);
    }
  }
  const types = [...counts].map(([type, n]) => (n > 1 ? `${type} ×${n}` : type)).join(', ');
  const found = items.length - failed;
  return [
    found > 0 && `${found} ${found === 1 ? 'record' : 'records'} · ${types}`,
    failed > 0 && `${failed} failed`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * One row per observation: rescans and overlapping collectors record the same fact many times.
 * Same key as the backend (kind, host, summary); without source records the host is unknown and
 * rows match on kind and summary alone. The first one stays, so a live log never re-prints it.
 */
export function distinctEvidence<
  T extends {
    kind: string;
    summary?: string;
    sourcePointer: string;
    sourceRecordId: string;
  },
>(
  evidence: readonly T[],
  sourceRecords: readonly { sourceRecordId: string; subject: string }[] = [],
): T[] {
  const subjects = new Map(sourceRecords.map((source) => [source.sourceRecordId, source.subject]));
  const seen = new Set<string>();
  return evidence.filter((item) => {
    const key = [
      item.kind,
      subjects.get(item.sourceRecordId) ?? '',
      item.summary ?? item.sourcePointer,
    ].join('\u0000');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function formatEvidenceLine(kind: string, summary?: string): string {
  if (kind === 'NETWORK_ENDPOINT') {
    const parsed = parseNetworkEvidence(summary);
    return parsed.type === 'NET' ? parsed.value : `${parsed.type} ${parsed.value}`;
  }
  if (kind === 'DNS_RECORD') {
    const parsed = parseEvidenceDns(summary);
    return `${parsed.type} ${parsed.value}`;
  }
  return summary ?? kind.replaceAll('_', ' ').toLowerCase();
}

export function formatSourceLabel(
  sourceRecords: readonly {
    sourceRecordId: string;
    sourceSystem?: string;
    sourceVersion?: string;
  }[],
  sourceRecordId?: string,
): string {
  if (!sourceRecordId) return '';
  const record = sourceRecords.find((item) => item.sourceRecordId === sourceRecordId);
  if (!record?.sourceSystem) return '';
  return collectorText(record.sourceSystem, record.sourceVersion);
}

export type FindingTaskLane = 'DNS' | 'Network' | 'Mixed';

export function findingId(finding: { findingId: string }): string {
  return finding.findingId.trim();
}

export function findingTitle(finding: { title: string }): string {
  return finding.title.trim();
}

export function isProgressFinding(row: unknown): row is {
  findingId: string;
  title: string;
  lane?: FindingTaskLane;
  normalizedSeverity?: string;
} {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  const finding = row as { findingId?: unknown; title?: unknown };
  return (
    typeof finding.findingId === 'string' &&
    finding.findingId.trim().length > 0 &&
    typeof finding.title === 'string' &&
    finding.title.trim().length > 0
  );
}

/**
 * Source-reported informational records such as DNS names, ports, ASNs and IPs are inventory.
 * Keep informational findings when they were judged or authored; INFORMATIONAL is a valid finding
 * severity.
 */
export function isSecurityFinding(finding: {
  normalizedSeverity?: string;
  claimOrigin?: string;
}): boolean {
  return (
    finding.normalizedSeverity !== 'INFORMATIONAL' ||
    finding.claimOrigin === 'MODEL_INFERRED' ||
    finding.claimOrigin === 'ANALYST_AUTHORED'
  );
}

export function findingTaskLane(
  finding: {
    evidenceIds?: string[];
    affectedAssets?: string[];
    title?: string;
    description?: string;
    lane?: FindingTaskLane;
  },
  evidence: readonly { evidenceId: string; kind: string }[],
): FindingTaskLane {
  if (finding.lane === 'DNS' || finding.lane === 'Network' || finding.lane === 'Mixed') {
    return finding.lane;
  }
  const linked = evidence.filter((item) => finding.evidenceIds?.includes(item.evidenceId));
  const hasDns = linked.some((item) => item.kind === 'DNS_RECORD');
  const hasNet = linked.some((item) => item.kind === 'NETWORK_ENDPOINT');
  if (hasDns && hasNet) return 'Mixed';
  if (hasNet) return 'Network';
  if (hasDns) return 'DNS';

  const haystack = `${finding.title ?? ''} ${finding.description ?? ''}`.toLowerCase();
  if (/\b(spf|dmarc|ptr|reverse dns)\b/.test(haystack)) return 'DNS';
  const looksNetwork =
    /\b(network|tcp|asn|https?|ssl|tls|port|hosting|endpoint|transparency)\b/.test(haystack) ||
    (finding.affectedAssets ?? []).some((asset) => /^\d/.test(asset));
  return looksNetwork ? 'Network' : 'DNS';
}

export function findingsCountText(
  findings: ReadonlyArray<{
    evidenceIds?: string[];
    affectedAssets?: string[];
    title?: string;
    description?: string;
    lane?: FindingTaskLane;
  }>,
  evidence: readonly { evidenceId: string; kind: string }[] = [],
): string {
  if (findings.length === 0) return 'None recorded';
  const counts: Record<FindingTaskLane, number> = { DNS: 0, Network: 0, Mixed: 0 };
  for (const finding of findings) {
    counts[findingTaskLane(finding, evidence)] += 1;
  }
  const parts = [];
  if (counts.DNS) parts.push(`${counts.DNS} DNS`);
  if (counts.Network) parts.push(`${counts.Network} Network`);
  if (counts.Mixed) parts.push(`${counts.Mixed} Mixed`);
  return `${findings.length} · ${parts.join(', ')}`;
}
